import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  InputBase,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StopAddMap from "./StopAddMap";

const CustomInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiInputBase-input": {
    borderRadius: 16,
    position: "relative",
    backgroundColor: theme.palette.mode === "light" ? "#fcfcfb" : "#2b2b2b",
    border: "2px solid #95A0B3",
    fontSize: 16,
    height: "auto",
    minHeight: 20,
    width: "100%", // Add this line
    maxWidth: "100%", // Add this line
    boxSizing: "border-box",
    padding: "10px 0px 10px 12px",
    transition: theme.transitions.create([
      "border-color",
      "background-color",
      "box-shadow",
    ]),
    "&:focus": {
      borderColor: "#1976d2",
    },
  },
}));

// Mandatory Field Label Component
const MandatoryLabel = ({ children }) => (
  <Typography variant="subtitle2" gutterBottom>
    {children}
    <span style={{ color: "red", marginLeft: "4px" }}>*</span>
  </Typography>
);

const toRadians = (deg) => (deg * Math.PI) / 180;

const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
  const earthRadiusKm = 6371;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getSitePoints = (site) => {
  if (!site?.sitCoordinates?.length) return [];
  return [...site.sitCoordinates]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => ({
      lat: parseFloat(c.latitude),
      lng: parseFloat(c.longitude),
    }))
    .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
};

const sanitizeCoordinateInput = (value) => {
  const text = String(value ?? "");
  const negative = text.trim().startsWith("-");
  const unsigned = text.replace(/-/g, "").replace(/[^\d.]/g, "");
  const [whole = "", ...fraction] = unsigned.split(".");
  const sanitized = fraction.length ? `${whole}.${fraction.join("")}` : whole;
  return negative ? `-${sanitized}` : sanitized;
};

const isCoordinateInRange = (value, min, max) => {
  const text = String(value ?? "").trim();
  if (!/^-?\d+(\.\d+)?$/.test(text)) return false;
  const number = Number(text);
  return number >= min && number <= max;
};

// Returns true when the point is inside the site boundary.
// When the boundary can't be determined (missing coordinates/radius),
// it returns true so we don't discard the user's manual values.
const isPointInsideSite = (lat, lng, site) => {
  const points = getSitePoints(site);
  if (!points.length) return true;

  const type = (site.boundaryType || "").toUpperCase();

  if (type === "CUSTOM" && points.length >= 3) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const xi = points[i].lng;
      const yi = points[i].lat;
      const xj = points[j].lng;
      const yj = points[j].lat;
      const denominator = yj - yi || Number.EPSILON;
      const intersect =
        yi > lat !== yj > lat &&
        lng < ((xj - xi) * (lat - yi)) / denominator + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }

  const radiusKm = parseFloat(site.radius || 0);
  if (!(radiusKm > 0)) return true;
  const center = points[0];
  return haversineDistanceKm(lat, lng, center.lat, center.lng) <= radiusKm;
};

export default function StopAddDetails({
  initialData = {},
  onSubmit,
  onCancel,
  onDelete,
  embedded = false,
  // onPositionChange,
  position,
  updateMapPosition,
  labels = [],
  selectedLabelId = "",
  onLabelChange,
  organizationId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stopDetails, setStopDetails] = useState({
    stopId: "",
    stopName: "",
    stopLongitude: "",
    stopLatitude: "",
    landmark: "",
    description: "",
    type: "STOP",
    siteId: "",
  });
  const [sites, setSites] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Track which field is being edited
  const [editingField, setEditingField] = useState(null);

  // Flag to prevent map updates during user editing
  const isUserEditing = useRef(false);

  // A flag to prevent map updates during initial data load
  const initialLoadComplete = useRef(false);

  // Flag to track if fields were manually cleared
  const fieldsManuallyCleared = useRef({
    stopLatitude: false,
    stopLongitude: false,
  });

  useEffect(() => {
    if (organizationId) {
      import("axios").then(({ default: axios }) => {
        import("../../config.js").then(({ default: config }) => {
          axios
            .get(`${config.baseURL}/site/organization`, {
              withCredentials: true,
              params: { organizationId },
            })
            .then((res) => setSites(res.data?.sites || res.data || []))
            .catch(() => {});
        });
      });
    }
  }, [organizationId]);

  const selectedSite = sites.find(
    (s) => String(s.id) === String(stopDetails.siteId)
  );

  useEffect(() => {
    if (initialData) {
      setStopDetails({
        stopId: initialData.stopId || "",
        stopName: initialData.name || "",
        stopLongitude: initialData.longitude || "",
        stopLatitude: initialData.latitude || "",
        landmark: initialData.landmark || "",
        description: initialData.description || "",
        type: initialData.type || "STOP",
        siteId: initialData.siteId || "",
      });

      // Reset manually cleared flags
      fieldsManuallyCleared.current = {
        stopLatitude: false,
        stopLongitude: false,
      };

      // Set the flag after initial load
      setTimeout(() => {
        initialLoadComplete.current = true;
      }, 500);
    }
  }, [initialData]);

  useEffect(() => {
    if (
      !position ||
      !initialLoadComplete.current ||
      isUserEditing.current
    )
      return;

    // Only update if not manually cleared and not currently editing
    if (
      editingField !== "stopLongitude" &&
      position.longitude &&
      !fieldsManuallyCleared.current.stopLongitude
    ) {
      setStopDetails((prev) => ({
        ...prev,
        stopLongitude: position.longitude,
      }));
    }

    if (
      editingField !== "stopLatitude" &&
      position.latitude &&
      !fieldsManuallyCleared.current.stopLatitude
    ) {
      setStopDetails((prev) => ({
        ...prev,
        stopLatitude: position.latitude,
      }));
    }
  }, [position, editingField]);

  useEffect(() => {
    const latitude = stopDetails.stopLatitude;
    const longitude = stopDetails.stopLongitude;
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    const hasCoords =
      latitude !== "" &&
      longitude !== "" &&
      !Number.isNaN(lat) &&
      !Number.isNaN(lng);
    const outside =
      Boolean(selectedSite) &&
      hasCoords &&
      !isPointInsideSite(lat, lng, selectedSite);
    const message = t("Stop must be inside the selected site");

    setErrors((prev) => {
      const next = { ...prev };
      const latitudeIsOutside = prev.stopLatitude === message;
      const longitudeIsOutside = prev.stopLongitude === message;
      if (outside) {
        if (latitudeIsOutside && longitudeIsOutside) return prev;
        next.stopLatitude = message;
        next.stopLongitude = message;
        return next;
      }
      if (!latitudeIsOutside && !longitudeIsOutside) return prev;
      if (latitudeIsOutside) delete next.stopLatitude;
      if (longitudeIsOutside) delete next.stopLongitude;
      return next;
    });
  }, [stopDetails.stopLatitude, stopDetails.stopLongitude, selectedSite, t]);

  const handleInputFocus = (e) => {
    const { name } = e.target;
    setEditingField(name);
    isUserEditing.current = true;

    // Reset the manually cleared flag when focusing again
    if (name === "stopLatitude" || name === "stopLongitude") {
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: false,
      };
    }
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    const rawValue = e.target.value;
    const value =
      name === "stopLatitude" || name === "stopLongitude"
        ? sanitizeCoordinateInput(rawValue)
        : rawValue;
    if (
      (name === "stopLatitude" || name === "stopLongitude") &&
      value !== rawValue
    ) {
      e.target.value = value;
    }

    // Mark field as manually cleared if value is empty
    if ((name === "stopLatitude" || name === "stopLongitude") && value === "") {
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: true,
      };
    } else if (name === "stopLatitude" || name === "stopLongitude") {
      // Reset manually cleared flag when value is provided
      fieldsManuallyCleared.current = {
        ...fieldsManuallyCleared.current,
        [name]: false,
      };
    }

    // Update the state for all fields
    setStopDetails((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "siteId") {
      const parsedLat = parseFloat(stopDetails.stopLatitude);
      const parsedLng = parseFloat(stopDetails.stopLongitude);
      const hasManualCoords =
        stopDetails.stopLatitude !== "" &&
        stopDetails.stopLongitude !== "" &&
        !Number.isNaN(parsedLat) &&
        !Number.isNaN(parsedLng);

      if (hasManualCoords) {
        updateMapPosition(parsedLat, parsedLng);
      }
    }

    // Only try to update map if both values exist and are valid numbers
    if (name === "stopLatitude" || name === "stopLongitude") {
      const lat = name === "stopLatitude" ? value : stopDetails.stopLatitude;
      const lng = name === "stopLongitude" ? value : stopDetails.stopLongitude;

      // Convert to strings for checking if they end with decimal
      const latStr = String(lat);
      const lngStr = String(lng);

      // Check if the input ends with a decimal point - still valid for input
      const isIncompleteDecimal = latStr.endsWith(".") || lngStr.endsWith(".");

      // Only update map if both values are valid numbers and not incomplete decimals
      if (lat && lng && !isIncompleteDecimal) {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);

        if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
          updateMapPosition(parsedLat, parsedLng);
        }
      }
    }
  };

  // Reset editing status after input blur
  const handleInputBlur = () => {
    // Use setTimeout to ensure the next field's focus is registered before clearing
    setTimeout(() => {
      setEditingField(null);
      isUserEditing.current = false;
    }, 200);
  };

  const validateForm = () => {
    const newErrors = {};
    const mandatoryFields = [
      "stopId",
      "stopName",
      "stopLongitude",
      "stopLatitude",
    ];

    mandatoryFields.forEach((field) => {
      if (!stopDetails[field]) {
        newErrors[field] = "This field is required";
      }
    });

    if (stopDetails.stopLatitude && !isCoordinateInRange(stopDetails.stopLatitude, -90, 90)) {
      newErrors.stopLatitude = t("Enter a valid latitude between -90 and 90");
    }
    if (stopDetails.stopLongitude && !isCoordinateInRange(stopDetails.stopLongitude, -180, 180)) {
      newErrors.stopLongitude = t("Enter a valid longitude between -180 and 180");
    }

    const latitude = parseFloat(stopDetails.stopLatitude);
    const longitude = parseFloat(stopDetails.stopLongitude);
    if (
      selectedSite &&
      !newErrors.stopLatitude &&
      !newErrors.stopLongitude &&
      !Number.isNaN(latitude) &&
      !Number.isNaN(longitude) &&
      !isPointInsideSite(latitude, longitude, selectedSite)
    ) {
      const outsideSiteMessage = t("Stop must be inside the selected site");
      newErrors.stopLatitude = outsideSiteMessage;
      newErrors.stopLongitude = outsideSiteMessage;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        stopId: stopDetails.stopId,
        name: stopDetails.stopName,
        longitude: stopDetails.stopLongitude,
        latitude: stopDetails.stopLatitude,
        landmark: stopDetails.landmark,
        description: stopDetails.description,
        type: stopDetails.type,
        siteId: stopDetails.siteId || null,
      });
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePositionChange = (lat, lng) => {
  // Call the parent's updateMapPosition function
  updateMapPosition(lat, lng);
};

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  return (
    <Box
      sx={{
        height: "auto",
        margin: embedded ? 0 : "40px",
        borderRadius: 2,
        padding: embedded ? "24px 0px" : "40px 0px",
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
        backgroundColor: "#ffffff",
      }}
    >
      <Grid container gap={2} justifyContent="center">
        <Grid
          item
          xs={12}
          sm={6}
          md={7}
          sx={{ display: "flex", flexDirection: "column", alignSelf: "stretch" }}
        >
          <Box
            sx={{
              minHeight: "43px",
              borderRadius: "12px",
              backgroundColor: "#647490",
              color: "white",
              p: "10px 20px",
              fontSize: "20px",
              mb: "20px",
              boxShadow: " -2px 2px 5px 0px #00000040",
            }}
          >
            <Typography variant="h6">{t("Basic Details")}</Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={6}>
              <MandatoryLabel>{t("Stop ID")}</MandatoryLabel>
              <TextField
                fullWidth
                name="stopId"
                value={stopDetails.stopId}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                variant="standard"
                placeholder={`${t("Type Stop ID")} (e.g.STP101)`}
                InputProps={{
                  disableUnderline: true,
                  inputComponent: CustomInput,
                }}
                error={!!errors.stopId}
                helperText={errors.stopId}
                required={true}
                FormHelperTextProps={{
                  sx: {
                    marginLeft: 0,
                    marginTop: 1,
                    position: "absolute",
                    bottom: "-1.5em",
                  },
                }}
                sx={{
                  position: "relative",
                  paddingBottom: "1.5em",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <MandatoryLabel>{t("Stop Name")}</MandatoryLabel>
              <TextField
                fullWidth
                name="stopName"
                value={stopDetails.stopName}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                variant="standard"
                placeholder={`${t("Type Stop Name")} (e.g.Terminal Exit)`}
                InputProps={{
                  disableUnderline: true,
                  inputComponent: CustomInput,
                }}
                error={!!errors.stopName}
                helperText={errors.stopName}
                required={true}
                FormHelperTextProps={{
                  sx: {
                    marginLeft: 0,
                    marginTop: 1,
                    position: "absolute",
                    bottom: "-1.5em",
                  },
                }}
                sx={{
                  position: "relative",
                  paddingBottom: "1.5em",
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ flex: 1, minHeight: { xs: "420px", md: "320px" }, position: "relative" }}>
            <Box sx={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}>
              <StopAddMap
                initialLatitude={position.latitude}
                initialLongitude={position.longitude}
                onPositionChange={updateMapPosition}
                sitePreview={selectedSite || null}
              />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Box
            sx={{
              minHeight: "43px",
              borderRadius: "12px",
              backgroundColor: "#647490",
              color: "white",
              fontSize: "20px",
              mb: "20px",
              p: "10px 20px",
              boxShadow: " -2px 2px 5px 0px #00000040",
            }}
          >
            <Typography variant="h6">{t("Location Details")}</Typography>
          </Box>

          {/* Stop Longitude field */}
          <Grid item xs={12} sm={6} md={12}>
            <MandatoryLabel>{t("Stop Longitude")}</MandatoryLabel>
            <TextField
              fullWidth
              name="stopLongitude"
              value={stopDetails.stopLongitude}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              variant="standard"
              placeholder={`${t("Type Stop Longitude")} (e.g.77.5946)`}
              InputProps={{
                disableUnderline: true,
                inputComponent: CustomInput,
              }}
              error={!!errors.stopLongitude}
              helperText={errors.stopLongitude}
              required={true}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginTop: 1,
                  position: "absolute",
                  bottom: "-1.5em",
                },
              }}
              sx={{
                position: "relative",
                paddingBottom: "1.5em",
              }}
            />
          </Grid>

          {/* Stop Latitude field */}
          <Grid item xs={12} sm={6} md={12} sx={{ mt: "20px" }}>
            <MandatoryLabel>{t("Stop Latitude")}</MandatoryLabel>
            <TextField
              fullWidth
              name="stopLatitude"
              value={stopDetails.stopLatitude}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              variant="standard"
              placeholder={`${t("Type Stop Latitude")} (e.g.12.9716)`}
              InputProps={{
                disableUnderline: true,
                inputComponent: CustomInput,
              }}
              error={!!errors.stopLatitude}
              helperText={errors.stopLatitude}
              required={true}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginTop: 1,
                  position: "absolute",
                  bottom: "-1.5em",
                },
              }}
              sx={{
                position: "relative",
                paddingBottom: "1.5em",
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={12}>
            <Box
              sx={{
                color: "#647490",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "100%",
                letterSpacing: "0",
                textAlign: "center",
                my: "20px",
              }}
            >
            
              {t("You can also add longitude and latitude by clicking on the map.")}
            </Box>
          </Grid>
          {/* Site dropdown */}
          <Grid item xs={12} sm={6} md={12} sx={{ mt: "20px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("Site")}
            </Typography>
            <FormControl fullWidth>
              <Select
                displayEmpty
                name="siteId"
                value={stopDetails.siteId || ""}
                onChange={handleInputChange}
                sx={{
                  borderRadius: "16px",
                  height: "48px",
                  backgroundColor: "#fcfcfb",
                  border: "2px solid #95A0B3",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover": { border: "2px solid #95A0B3" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused": { border: "2px solid #1976d2" },
                }}
              >
                <MenuItem value="">
                  <em>{t("Select Site")}</em>
                </MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={String(site.id)}>
                    {site.name || site.siteId || `Site ${site.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Box
            sx={{
              minHeight: "43px",
              borderRadius: "12px",
              backgroundColor: "#647490",
              color: "white",
              mt: "24px",
              mb: "20px",
              p: "10px 20px",
              width: "100%",
              boxShadow: " -2px 2px 5px 0px #00000040",
            }}
          >
            <Typography variant="h6">{t("Other Details")}</Typography>
          </Box>


          {/* Label dropdown */}
          <Grid item xs={12} sm={6} md={12} sx={{ mb: "20px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("Label")}
            </Typography>
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={selectedLabelId}
                onChange={(e) => onLabelChange && onLabelChange(e.target.value)}
                sx={{
                  borderRadius: "16px",
                  height: "48px",
                  backgroundColor: "#fcfcfb",
                  border: "2px solid #95A0B3",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover": { border: "2px solid #95A0B3" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused": { border: "2px solid #1976d2" },
                }}
              >
                <MenuItem value="">
                  <em>{t("Select Label")}</em>
                </MenuItem>
                {labels.map((label) => (
                  <MenuItem key={label.id} value={String(label.id)}>
                    {label.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Landmark field */}
          <Grid item xs={12} sm={6} md={12}>
            <Typography variant="subtitle2" gutterBottom>
              {t("Landmark")}
            </Typography>
            <TextField
              fullWidth
              name="landmark"
              value={stopDetails.landmark}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              variant="standard"
              placeholder={`${t("Type Landmark")} (e.g.Near Runway Gate)`}
              InputProps={{
                disableUnderline: true,
                inputComponent: CustomInput,
              }}
              error={!!errors.landmark}
              helperText={errors.landmark}
              required={false}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginTop: 1,
                  position: "absolute",
                  bottom: "-1.5em",
                },
              }}
              sx={{
                position: "relative",
                paddingBottom: "1.5em",
              }}
            />
          </Grid>

          {/* Description field */}
          <Grid item xs={12} sm={6} md={12} sx={{ mt: "20px" }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("Description")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              name="description"
              value={stopDetails.description}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              variant="standard"
              placeholder={t("Type Description")}
              InputProps={{
                disableUnderline: true,
                // inputComponent: CustomInput,
              }}
              error={!!errors.description}
              helperText={errors.description}
              required={false}
              FormHelperTextProps={{
                sx: {
                  marginLeft: 0,
                  marginTop: 1,
                  position: "absolute",
                  bottom: "-1.5em",
                },
              }}
              sx={{
                position: "relative",
                paddingBottom: "1.5em",
                "& .MuiInputBase-root": {
                  borderRadius: "16px",
                  backgroundColor: "#fcfcfb",
                  border: "2px solid #95A0B3",
                  padding: "10px 12px",
                  "&:focus-within": {
                    borderColor: "#1976d2",
                  },
                },
              }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12} container justifyContent="center" spacing={2}>
          {embedded && onDelete && (
            <Grid item>
              <Button
                onClick={onDelete}
                disabled={isSubmitting}
                sx={{
                  width: "122px",
                  fontWeight: 600,
                  textTransform: "none",
                  height: "55px",
                  borderRadius: "8px",
                  fontSize: "20px",
                  color: "#F20000",
                  background: "#FFFFFF",
                  border: "1px solid #F20000",
                  "&:hover": { backgroundColor: "#FFF0F0" },
                }}
              >
                {t("Delete")}
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                width: "122px",
                fontWeight: 600,
                textTransform: "none",
                height: "55px",
                borderRadius: "8px",
                borderWidth: 1,
                paddingTop: "16px",
                paddingRight: "24px",
                paddingBottom: "16px",
                paddingLeft: "24px",
                background: "#F2F2F3",
                border: "1px solid #414958",
                fontSize: "20px",
                color: "#1F242E",
                "&:hover": {
                  background: "#E0E0E1",
                },
              }}
            >
              {t("Cancel")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                fontWeight: 600,
                width: "125px",
                fontSize: "20px",
                color: "#1F242E",
                textTransform: "none",
                height: "55px",
                borderRadius: "8px",
                borderWidth: 1,
                paddingTop: "16px",
                paddingRight: "24px",
                paddingBottom: "16px",
                paddingLeft: "24px",
                background: "#F9E719",
                border: "1px solid #414958",
                ":hover": {
                  background: "#F9E718",
                },
              }}
            >
              {t("Save")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
